import { useContext } from 'react'
import { PassContext } from "../../pass-context"
import dataSelector from '../data-selector'
import PopupEditor from '../popup-editor'
import fieldFormatter from '../fronts/components/field-formatter'
import parseHtml from 'html-react-parser'
import sanitize from 'sanitize-html'

import IconEmail from '@mui/icons-material/EmailOutlined'
import IconLocation from '@mui/icons-material/LocationOn'
import IconWWW from '@mui/icons-material/Public'
import IconPhone from '@mui/icons-material/LocalPhone'

import UserSettings from './user-settings.jsx'
import TrainItinerary from './auto-fields/train-itinerary.jsx'

const Backfields = ({ show, panelColour }) => {
  try {
    const { classData, passType, subType, variables } = useContext(PassContext)

    let fields = classData.classTemplateInfo?.detailsTemplateOverride?.detailsItemInfos

    return (
      <>
        <TrainItinerary passType={passType} subType={subType} variables={variables} />

        <div className="fields">
          <PopupEditor title="Back Fields" path="backFields" type="fields">
            <div className="editable fields flex flex-col gap-[3px]">
              {fields?.map((f, idx) => <Item key={idx} item={f.item} bgColour={panelColour}/>)}
            </div>
          </PopupEditor>
        </div>

        <UserSettings show={show} />
      </>
    )
  }
  catch (err) {
    throw err
  }
}

export default Backfields



const Item = ({ item }) => {

  try {
    const { classData, objectData, variables } = useContext(PassContext)

    const ds = (item.firstValue.fields[0].fieldPath.replace(/(object|class).*/, "$1"))
    const dataSource = ds === "class" ? classData : objectData
    const keys = (item.firstValue.fields[0].fieldPath.replace(/(object|class)\.([^\[]*)\[['"]([^'"]*)['"]\]/, "$2,$3")).split(",")

    const dataType = keys[0]
    const key = keys[1]
    const source = dataType === "textModulesData" ? dataSource.textModulesData ?? [] : dataSource.linksModuleData?.uris ?? []

    const itemData = source?.filter(m => m.id === key)[0]

    const resolvedItemdata = dataSelector({ objectData: { itemData }, variables, path: "itemData" })

    if (itemData) {
      switch (resolvedItemdata.type ? resolvedItemdata.type : "text") { // Use text type if not specified
        case "text": return (
          <div className="field" key={key}>
            <div className="label">{resolvedItemdata.header}</div>
            <div className="text">{formatTextField(resolvedItemdata.body)}</div>
          </div>
        )

        case "number":
        case "money":
        case "date":
        case "time":
        case "datetime": return (
          <div className="field" key={key}>
            <div className="label">{resolvedItemdata.header}</div>
            <div className="text">{fieldFormatter(resolvedItemdata)}</div>
          </div>
        )

        default: return (
          <div className="linkfield" key={key}>
            <LinkIcon type={resolvedItemdata.type} />
            <LinkTag
              type={resolvedItemdata.type}
              label={resolvedItemdata.description || resolvedItemdata.header || resolvedItemdata.uri || resolvedItemdata.body || ""}
              value={resolvedItemdata.uri ?? resolvedItemdata.body ?? ""}
            />
          </div>
        )
      }
    }
  }
  catch (err) {

    throw err
  }
}

const LinkIcon = ({ type }) => {
  switch (type) {
    case "phone": return <IconPhone className="icon" />
    case "www": return <IconWWW className="icon" />
    case "email": return <IconEmail className="icon" />
    case "location": return <IconLocation className="icon" />
  }
}

const LinkTag = ({ type, label, value }) => {
  switch (type) { // Default to text type
    case "phone": return (<a href={`tel:${value}`} className="label">{label}</a>)
    case "www": return (<a href={`${value}`} target="_blank" className="label">{label}</a>)
    case "email": return (<a href={`mailto:${value}`} className="label">{label}</a>)
    case "location": return (<a href={`http://maps.google.com?q=${value}`} target="_blank" className="label">{label}</a>)
  }
}

const formatTextField = text => {
  const text1 = text?.toString().length ? text : "..."
  const text2 = cleanHtml(text1, ['a', 'br', 'b', 'i'])
  const text3 = newLinesToBrTags(text2)
  const text4 = tabsToNbsp(text3)
  const text5 = parseHtml(text4.toString())

  return text5
}

// Removes unwanted HTML tags / attributes
const cleanHtml = (html, allowedTags) =>
  sanitize(html?.toString() ?? "", {
    allowedTags: allowedTags,
    allowedAttributes: {
      'a': ['href', 'target']
    },
    selfClosing: ['br'],
    disallowedTagsMode: 'discard',
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
  })

const newLinesToBrTags = text => text?.replace(/\n/g, "<br/>")
const tabsToNbsp = text => text?.replace(/\t/g, "&nbsp;&nbsp;&nbsp;")
